/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';

import { useFlags, SecurityHelper } from 'sarsaparilla';
import { OrderConfirmationPage } from 'shared-ui/components/OrderConfirmationPage/OrderConfirmationPage';

import { getAppleWalletUrl } from '../../api/endpoints';

import { toMoment, convertMilitaryTime, toLocalTime } from '../../utils/dateUtils';
import { getOrderDetailsView, getQRCodes } from '../../actions/orderDetailsActions';
import { fetchFacilityData } from '../../actions/facilityDataActions';

function getTicketQuantity(ticketCountByGuestId) {
    if (isEmpty(ticketCountByGuestId)) {
        return '';
    }

    return Object.keys(ticketCountByGuestId)
        .reduce((acc, bucket) => {
            return acc + ticketCountByGuestId[bucket];
        }, 0)
        .toString();
}

export function CartlessConfirmationContainer() {
    const dispatch = useDispatch();
    const { ticketAdjustMultiDayTicket } = useFlags();
    const { reservationId } = useParams();
    const orderDetailsData = useSelector((state) => state.timedEntry.orderDetails) || {};
    const reservation =
        useSelector((state) => state.timedEntry.orderDetails?.reservation) || {};
    const priceSummary =
        useSelector((state) => state.timedEntry.orderDetails?.price_summary) || {};
    const facilityData = useSelector((state) => state.timedEntry.facility) || {};
    const email = useSelector((state) => state.auth.account?.account?.email);
    const { parent_asset_name: parentName, facility_name: facilityName } = facilityData;
    const { tour_date: tourDate, tour_time: tourTime, order_id: orderId } = reservation;
    const {
        google_pass_link: googlePassLink,
        tour_name: tourName,
        tour_duration: tourDuration,
        qrCodes,
    } = orderDetailsData;
    const { total_cost: totalCost } = priceSummary;
    const totalCostString = totalCost ? totalCost.toString() : '';

    const ticketCountByGuestId = reservation?.ticket_count_by_guest_type_id || {};
    const ticketQuantity = getTicketQuantity(ticketCountByGuestId);
    const tourEnd = toMoment(tourDate, tourTime).add(tourDuration, 'minutes');
    const validDays = useMemo(() => {
        return reservation?.valid_days || 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservation?.valid_days]);
    const allowMultiDays = useMemo(() => {
        return ticketAdjustMultiDayTicket && validDays > 1;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketAdjustMultiDayTicket, validDays]);

    let qrCode = null;
    let appleWalletLink = null;
    if (SecurityHelper.isGuest()) {
        qrCode = !isEmpty(Object.values(qrCodes)) ? Object.values(qrCodes)[0] : null;

        if (!isEmpty(qrCodes)) {
            const ticketId = Object.keys(qrCodes)[0];
            appleWalletLink = getAppleWalletUrl(ticketId);
        }
    }

    const resObject = {
        order_details: {
            tour_date: tourDate,
            tour_start: convertMilitaryTime(tourTime),
            tour_end: convertMilitaryTime(toLocalTime(tourEnd)),
            description: tourName,
            recarea: parentName,
            location: facilityName,
            valid_days: validDays,
            allow_multidays: allowMultiDays,
            qrCode,
            googlePassLink,
            appleWalletLink,
        },
        subtotal: totalCostString,
        quantity: ticketQuantity,
    };

    useEffect(() => {
        dispatch(getOrderDetailsView(reservationId));
        if (SecurityHelper.isGuest()) {
            dispatch(getQRCodes(reservationId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationId]);

    useEffect(() => {
        dispatch(fetchFacilityData(orderDetailsData?.facility_id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderDetailsData.facility_id]);

    return (
        <OrderConfirmationPage
            email={email}
            subtotal={totalCostString}
            tax="0"
            total={totalCostString}
            modification
            orderID={orderId}
            reservations={[resObject]}
        />
    );
}

//cSpell:ignore recarea, multidays
